/**
 * Business HQ spend (`/api/admin/v2/business`) and the caller's own expense
 * claims (`/api/admin/v2/workplace/expenses*`). `accounting.view` reads,
 * `accounting.manage` changes. Receipts come back as short-lived signed links.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type {
  Bill,
  BizContract,
  BizVendor,
  BudgetView,
  Budget,
  Expense,
  ExpenseCategory,
  IntegrationCandidate,
  OtherIncome,
  BizAsset,
  ComplianceItem,
  PurchaseOrderV2,
  AccountingOverview,
  CashReport,
  PnlReport,
  RevenueView,
  RecurringCharge,
  WorkplaceExpenses,
} from '@/client/types/business/spend';
import { WORKPLACE_BASE, hrKeys, useHrApi } from '../hr/people';

const BASE = '/api/admin/v2/business';

export const spendKeys = {
  all: ['business-spend'] as const,
  vendors: () => [...spendKeys.all, 'vendors'] as const,
  candidates: () => [...spendKeys.all, 'candidates'] as const,
  categories: () => [...spendKeys.all, 'categories'] as const,
  contracts: () => [...spendKeys.all, 'contracts'] as const,
  recurring: () => [...spendKeys.all, 'recurring'] as const,
  bills: () => [...spendKeys.all, 'bills'] as const,
  expenses: () => [...spendKeys.all, 'expenses'] as const,
  budgets: (from: string, to: string) => [...spendKeys.all, 'budgets', from, to] as const,
  mine: () => [...hrKeys.workplace(), 'expenses'] as const,
  revenue: (from: string, to: string) => [...spendKeys.all, 'revenue', from, to] as const,
};

const q = { staleTime: 15_000, gcTime: DEFAULT_GC_TIME };

function useList<T>(key: readonly unknown[], path: string, field: string, enabled = true) {
  const api = useHrApi(BASE);
  return useQuery({
    queryKey: key,
    queryFn: async () => ((await api<Record<string, T[]>>(path))[field] ?? []) as T[],
    enabled,
    ...q,
  });
}

export const useVendorsQuery = (enabled = true) => useList<BizVendor>(spendKeys.vendors(), '/vendors', 'vendors', enabled);
export const useVendorCandidatesQuery = (enabled: boolean) =>
  useList<IntegrationCandidate>(spendKeys.candidates(), '/vendors/import-candidates', 'candidates', enabled);
export const useCategoriesQuery = (enabled = true) =>
  useList<ExpenseCategory>(spendKeys.categories(), '/categories', 'categories', enabled);
export const useContractsQuery = () => useList<BizContract>(spendKeys.contracts(), '/contracts', 'contracts');
export const useRecurringQuery = () => useList<RecurringCharge>(spendKeys.recurring(), '/recurring', 'recurring');
export const useBillsQuery = () => useList<Bill>(spendKeys.bills(), '/bills', 'bills');
export const useExpensesQuery = () => useList<Expense>(spendKeys.expenses(), '/expenses', 'expenses');

export const useBudgetsQuery = (from: string, to: string) => {
  const api = useHrApi(BASE);
  return useQuery({ queryKey: spendKeys.budgets(from, to), queryFn: () => api<BudgetView>(`/budgets?from=${from}&to=${to}`), ...q });
};

function useMut<TIn, TOut>(base: string, fn: (api: ReturnType<typeof useHrApi>, input: TIn) => Promise<TOut>) {
  const api = useHrApi(base);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TIn) => fn(api, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: spendKeys.all });
      void qc.invalidateQueries({ queryKey: hrKeys.workplace() });
    },
  });
}

const upsert =
  <T,>(path: string) =>
  (api: ReturnType<typeof useHrApi>, { id, ...body }: Partial<T> & { id?: string }) =>
    id ? api<T>(`${path}/${encodeURIComponent(id)}`, 'PATCH', body) : api<T>(path, 'POST', body);

export const useUpsertVendorMutation = () => useMut(BASE, upsert<BizVendor>('/vendors'));
export const useImportVendorsMutation = () =>
  useMut(BASE, (api, items: { integrationName: string; vendorName: string }[]) =>
    api<{ created: string[]; linked: string[]; skipped: unknown[] }>('/vendors/import', 'POST', { items }),
  );
export const useUpsertCategoryMutation = () => useMut(BASE, upsert<ExpenseCategory>('/categories'));
export const useUpsertContractMutation = () => useMut(BASE, upsert<BizContract>('/contracts'));
export const useUpsertRecurringMutation = () => useMut(BASE, upsert<RecurringCharge>('/recurring'));
export const useRunRecurringMutation = () =>
  useMut(BASE, (api, _: void) => api<{ charges: number; billsCreated: number }>('/recurring/run-due', 'POST'));
export const useUpsertBillMutation = () => useMut(BASE, upsert<Bill>('/bills'));
export const useBillActionMutation = () =>
  useMut(BASE, (api, { id, action, ...body }: { id: string; action: 'approve' | 'pay' | 'void'; paidOn?: string; paymentRef?: string }) =>
    api<Bill>(`/bills/${encodeURIComponent(id)}/${action}`, 'POST', body),
  );
export const useAttachBillMutation = () =>
  useMut(BASE, (api, { id, file }: { id: string; file: File }) => {
    const form = new FormData();
    form.append('file', file);
    return api<Bill>(`/bills/${encodeURIComponent(id)}/attachment`, 'POST', form);
  });
export const useDecideExpenseMutation = () =>
  useMut(BASE, (api, { id, ...body }: { id: string; decision: 'approve' | 'reject'; note?: string }) =>
    api<Expense>(`/expenses/${encodeURIComponent(id)}/decide`, 'POST', body),
  );
export const useReimburseExpenseMutation = () =>
  useMut(BASE, (api, { id, reference }: { id: string; reference?: string }) =>
    api<Expense>(`/expenses/${encodeURIComponent(id)}/reimburse`, 'POST', { reference }),
  );
export const useUpsertBudgetMutation = () => useMut(BASE, upsert<Budget>('/budgets'));
export const useDeleteBudgetMutation = () =>
  useMut(BASE, (api, id: string) => api<{ id: string }>(`/budgets/${encodeURIComponent(id)}`, 'DELETE'));

/** Fetch a signed link and open it. `path` is relative to the base given. */
export function useOpenDocument(base: 'business' | 'workplace') {
  const api = useHrApi(base === 'business' ? BASE : WORKPLACE_BASE);
  return async (path: string) => {
    const { url } = await api<{ url: string }>(path);
    window.open(url, '_blank', 'noopener');
  };
}

// My workplace — always the caller.

export const useMyExpensesQuery = () => {
  const api = useHrApi(WORKPLACE_BASE);
  return useQuery({ queryKey: spendKeys.mine(), queryFn: () => api<WorkplaceExpenses>('/expenses'), ...q });
};

export const useFileExpenseMutation = () =>
  useMut(WORKPLACE_BASE, (api, { file, ...fields }: { spentOn: string; categoryId?: string; description: string; amount: number; currency: string; file?: File | null }) => {
    if (!file) return api<Expense>('/expenses', 'POST', fields);
    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => v !== undefined && v !== null && form.append(k, String(v)));
    form.append('file', file);
    return api<Expense>('/expenses', 'POST', form);
  });

export const useCancelMyExpenseMutation = () =>
  useMut(WORKPLACE_BASE, (api, id: string) => api<Expense>(`/expenses/${encodeURIComponent(id)}/cancel`, 'POST'));

export const useManagerDecideExpenseMutation = () =>
  useMut(WORKPLACE_BASE, (api, { id, ...body }: { id: string; decision: 'approve' | 'reject'; note?: string }) =>
    api<Expense>(`/expense-approvals/${encodeURIComponent(id)}/decide`, 'POST', body),
  );

// ------------------------------------------------------------------ revenue

export const useRevenueQuery = (from: string, to: string) => {
  const api = useHrApi(BASE);
  return useQuery({ queryKey: spendKeys.revenue(from, to), queryFn: () => api<RevenueView>(`/revenue?from=${from}&to=${to}`), ...q });
};

export const useUpsertOtherIncomeMutation = () => useMut(BASE, upsert<OtherIncome>('/other-income'));
export const useDeleteOtherIncomeMutation = () =>
  useMut(BASE, (api, id: string) => api<{ id: string }>(`/other-income/${encodeURIComponent(id)}`, 'DELETE'));

// ------------------------------------------------------------------ reports

export const usePnlQuery = (from: string, to: string) => {
  const api = useHrApi(BASE);
  return useQuery({ queryKey: [...spendKeys.all, 'pnl', from, to], queryFn: () => api<PnlReport>(`/reports/pnl?from=${from}&to=${to}`), ...q });
};

export const useCashFlowQuery = (from: string, to: string) => {
  const api = useHrApi(BASE);
  return useQuery({ queryKey: [...spendKeys.all, 'cash', from, to], queryFn: () => api<CashReport>(`/reports/cash-flow?from=${from}&to=${to}`), ...q });
};

export const useAccountingOverviewQuery = () => {
  const api = useHrApi(BASE);
  return useQuery({ queryKey: [...spendKeys.all, 'overview'], queryFn: () => api<AccountingOverview>('/reports/overview'), ...q });
};

// ------------------------------------------- compliance, equipment, purchase orders

export const useComplianceQuery = () => useList<ComplianceItem>([...spendKeys.all, 'compliance'], '/compliance', 'items');
export const useAssetsQuery = () => useList<BizAsset>([...spendKeys.all, 'assets'], '/assets', 'assets');
export const usePurchaseOrdersQuery = () => useList<PurchaseOrderV2>([...spendKeys.all, 'pos'], '/purchase-orders', 'orders');

export const useAssetHistoryQuery = (id?: string) => {
  const api = useHrApi(BASE);
  return useQuery({
    queryKey: [...spendKeys.all, 'asset-history', id],
    queryFn: async () => (await api<{ history: { employeeId: string; assignedOn: string; returnedOn?: string | null; note: string }[] }>(`/assets/${id}/history`)).history,
    enabled: Boolean(id),
    ...q,
  });
};

export const useUpsertComplianceMutation = () => useMut(BASE, upsert<ComplianceItem>('/compliance'));
export const useAttachComplianceMutation = () =>
  useMut(BASE, (api, { id, file }: { id: string; file: File }) => {
    const f = new FormData();
    f.append('file', file);
    return api<ComplianceItem>(`/compliance/${id}/document`, 'POST', f);
  });
export const useUpsertAssetMutation = () => useMut(BASE, upsert<BizAsset>('/assets'));
export const useAssignAssetMutation = () =>
  useMut(BASE, (api, { id, employeeId, note }: { id: string; employeeId: string | null; note?: string }) =>
    api<BizAsset>(`/assets/${id}/assign`, 'POST', { employeeId, note }),
  );
export const useUpsertPoMutation = () => useMut(BASE, upsert<PurchaseOrderV2>('/purchase-orders'));
export const usePoActionMutation = () =>
  useMut(BASE, (api, { id, action }: { id: string; action: 'submit' | 'approve' | 'order' | 'receive' | 'cancel' }) =>
    api<PurchaseOrderV2>(`/purchase-orders/${id}/${action}`, 'POST'),
  );
